import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useManagedProfile } from "@/hooks/useManagedProfile";

// Define the expected response type from our database function
interface DeleteEntryResponse {
  success: boolean;
  error?: string;
  entry_title?: string;
  deleted_notifications_count?: number;
  delete_reason?: string;
}

export const useEnhancedTimelineEntryManagement = (timelineOwnerId?: string) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isHiding, setIsHiding] = useState(false);
  const queryClient = useQueryClient();
  const { isManagedProfile } = useManagedProfile(timelineOwnerId);

  const deleteEntry = async (entryId: string, requestingUserId: string, entryCreatorId: string, timelineOwnerId: string) => {
    setIsDeleting(true);
    try {
      console.log('Starting enhanced deletion process for entry:', entryId);

      // Use the enhanced database function with proper permission checking
      const { data, error } = await supabase.rpc('delete_timeline_entry_with_notifications', {
        entry_id_param: entryId,
        requesting_user_id: requestingUserId
      });

      if (error) {
        console.error('Error calling deletion function:', error);
        toast.error('Failed to delete entry');
        return false;
      }

      // Cast the data to our expected response type
      const response = data as unknown as DeleteEntryResponse;

      if (!response.success) {
        console.error('Deletion function returned error:', response.error);
        toast.error(response.error || 'Failed to delete entry');
        return false;
      }

      console.log('Entry deleted successfully:', {
        entryTitle: response.entry_title,
        deletedNotifications: response.deleted_notifications_count,
        deleteReason: response.delete_reason
      });

      // Show different success messages based on delete reason
      const getSuccessMessage = () => {
        switch (response.delete_reason) {
          case 'creator':
            return 'Your entry has been deleted successfully';
          case 'timeline_owner':
            return 'Entry removed from your timeline';
          case 'managed_profile_owner':
            return 'Entry deleted from managed profile';
          default:
            return 'Entry deleted successfully';
        }
      };

      // Invalidate queries to refresh the timeline
      await queryClient.invalidateQueries({ queryKey: ['timeline-entries', timelineOwnerId] });
      toast.success(getSuccessMessage());
      return true;
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Failed to delete entry');
      return false;
    } finally {
      setIsDeleting(false);
    }
  };

  const hideEntry = async (entryId: string, timelineOwnerId: string, isHidden: boolean) => {
    setIsHiding(true);
    try {
      // Use the existing is_approved field to hide/show entries
      const { error } = await supabase
        .from('profile_timeline_entries')
        .update({ 
          is_approved: !isHidden,
          approval_notes: isHidden ? 'Hidden by timeline owner' : null
        })
        .eq('id', entryId);

      if (error) {
        console.error('Error hiding/showing entry:', error);
        toast.error(`Failed to ${isHidden ? 'hide' : 'show'} entry`);
        return false;
      }

      // Invalidate queries to refresh the timeline
      await queryClient.invalidateQueries({ queryKey: ['timeline-entries', timelineOwnerId] });
      toast.success(`Entry ${isHidden ? 'hidden' : 'shown'} successfully`);
      return true;
    } catch (error) {
      console.error('Error hiding/showing entry:', error);
      toast.error(`Failed to ${isHidden ? 'hide' : 'show'} entry`);
      return false;
    } finally {
      setIsHiding(false);
    }
  };

  // Enhanced permission checking
  const canDeleteEntry = (entryCreatorId: string, timelineOwnerId: string, currentUserId: string): boolean => {
    // Entry creator can always delete
    if (entryCreatorId === currentUserId) return true;
    
    // Timeline owner can delete entries on their timeline
    if (timelineOwnerId === currentUserId) return true;
    
    // Managed profile owner can delete entries on managed profiles
    if (isManagedProfile) return true;
    
    return false;
  };

  const getDeleteButtonText = (entryCreatorId: string, timelineOwnerId: string, currentUserId: string): string => {
    if (entryCreatorId === currentUserId) return 'Delete';
    if (timelineOwnerId === currentUserId) return 'Remove';
    if (isManagedProfile) return 'Delete';
    return 'Delete';
  };

  return {
    deleteEntry,
    hideEntry,
    isDeleting,
    isHiding,
    canDeleteEntry,
    getDeleteButtonText,
    isManagedProfile
  };
};